/**
 * Startups Dashboard page.
 *
 * Server-side filtered + paginated startup list.
 * Filters sidebar (left) + table (right) layout.
 * Calls GET /api/v1/admin/startups with filter params.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useDebouncedSearch,
  getErrorMessage,
  captureException,
  getPageNumbers,
} from '@startus/lib';
import { Toast } from '@startus/ui';
import type { AdminStartupItem, PaginationMeta, StartupFilters } from '../types/startup';
import { startupsService } from '../services/startups.service';
import StartupsTable from '../components/StartupsTable';
import StartupDetailView from '../components/StartupDetailModal';
import StartupsFilterPanel from '../components/StartupsFilterPanel';
import { useStartupsCsvExport } from '../hooks/useStartupsCsvExport';

const PAGE_SIZE = 10;

type StartupSortOption = {
  labelKey: string;
  orderBy: 'name' | 'createdAt';
  sortDir: 'asc' | 'desc';
};

const SORT_OPTIONS: StartupSortOption[] = [
  { labelKey: 'startups.sort.newest', orderBy: 'createdAt', sortDir: 'desc' },
  { labelKey: 'startups.sort.oldest', orderBy: 'createdAt', sortDir: 'asc' },
  { labelKey: 'startups.sort.nameAsc', orderBy: 'name', sortDir: 'asc' },
  { labelKey: 'startups.sort.nameDesc', orderBy: 'name', sortDir: 'desc' },
];

const StartupsDashboard: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { state: routerState } = useLocation();

  const [startups, setStartups] = useState<AdminStartupItem[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedSearch(searchTerm);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<StartupFilters>({});
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [activeSortIndex, setActiveSortIndex] = useState(0);
  const [showSort, setShowSort] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  const activeFilterCount = Object.values(filters).filter((v) =>
    Array.isArray(v) ? v.length > 0 : !!v
  ).length;

  const { exportCsv, exporting, exportError } = useStartupsCsvExport(filters, searchTerm);
  const [exportToast, setExportToast] = useState<{ message: string; type: 'error' } | null>(null);

  // Translate export errors into a toast notification when they occur
  useEffect(() => {
    if (exportError != null) {
      captureException(exportError);
      setExportToast({ message: getErrorMessage(exportError, t), type: 'error' });
    }
  }, [exportError, t]);

  // Restore startup detail when navigating back from ApplicationsPage (via router state)
  const [selectedStartup, setSelectedStartup] = useState<AdminStartupItem | null>(
    (routerState as { startup?: AdminStartupItem } | null)?.startup ?? null
  );

  // Scroll the main container to top whenever a startup detail is opened
  useEffect(() => {
    if (selectedStartup) {
      document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedStartup]);

  const load = useCallback(async () => {
    const { orderBy, sortDir } = SORT_OPTIONS[activeSortIndex];
    setLoading(true);
    setError(null);
    try {
      const result = await startupsService.getAdminStartups({
        page: currentPage,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        orderBy,
        sortDir,
        ...filters,
      });
      setStartups(result.data);
      setMeta(result.meta);
    } catch (err) {
      captureException(err);
      setError(getErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, activeSortIndex, filters, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setShowSort(false);
    };
    if (showSort) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSort]);

  const handleFilterChange = (updated: StartupFilters) => {
    setFilters(updated);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  if (selectedStartup) {
    return (
      <StartupDetailView
        startup={selectedStartup}
        onClose={() => setSelectedStartup(null)}
        onViewApplications={() =>
          navigate('/admin/applications', {
            state: { startupId: selectedStartup.id, startup: selectedStartup },
          })
        }
      />
    );
  }

  return (
    <div className="card bg-[#1E1E1E] shadow-xl">
      <div className="card-body">
        <div className="mb-4">
          <h1 className="card-title text-2xl font-bold mb-1">{t('startups.title')}</h1>
          <p className="text-base-content/70 text-sm">{t('startups.subtitle')}</p>
        </div>

        {/* Search + filter toggle row — always visible */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative">
            <svg
              className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder={t('startups.search.placeholder')}
              aria-label={t('startups.search.label')}
              className="input input-bordered input-sm pl-8 h-8 w-48 text-xs"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div ref={sortRef} className="relative">
            <button
              type="button"
              className={`btn btn-ghost btn-square btn-sm${activeSortIndex !== 0 ? ' text-primary' : ''}`}
              aria-label={t('startups.sort.label')}
              onClick={() => setShowSort((v) => !v)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                />
              </svg>
            </button>

            {showSort && (
              <div className="absolute left-0 top-full mt-1 bg-[#1E1E1E] border border-base-300 rounded-2xl shadow-xl z-20 min-w-65 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-sm">{t('startups.sort.label')}</span>
                  <button
                    type="button"
                    className="text-base-content/60 hover:text-base-content transition-colors"
                    onClick={() => setShowSort(false)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="flex flex-col gap-1">
                  {SORT_OPTIONS.map((opt, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                        idx === activeSortIndex
                          ? 'bg-primary/20 text-primary font-medium'
                          : 'bg-base-300 hover:bg-base-200'
                      }`}
                      onClick={() => {
                        setActiveSortIndex(idx);
                        setCurrentPage(1);
                        setShowSort(false);
                      }}
                    >
                      {t(opt.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            className="flex items-center gap-2 px-3 h-8 text-xs font-medium rounded-lg border border-base-200 hover:bg-base-200 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4h18M7 8h10M11 12h4"
              />
            </svg>
            {t('startups.filters.title')}
            {activeFilterCount > 0 && (
              <span className="badge badge-primary badge-xs text-white">{activeFilterCount}</span>
            )}
            <svg
              className={`w-3 h-3 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => handleFilterChange({})}
              className="flex items-center gap-1.5 px-3 h-8 text-xs font-medium rounded-lg text-error border border-error/30 hover:bg-error/10 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              {t('startups.filters.clear')}
              <span className="badge badge-error badge-xs text-white">{activeFilterCount}</span>
            </button>
          )}

          {/* Export XLSX — always visible, reflects current filters */}
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            className="ml-auto flex items-center gap-1.5 px-3 h-8 text-xs font-medium rounded-lg border border-base-200 hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
                />
              </svg>
            )}
            {exporting
              ? t('startups.export.exporting', 'Exporting…')
              : t('startups.export.button', 'Export')}
          </button>
        </div>

        {/* Filter panel — toggled below the search row */}
        {filtersOpen && (
          <div className="mb-4 pb-4 border-b border-base-200">
            <StartupsFilterPanel filters={filters} onChange={handleFilterChange} />
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center h-40">
            <span className="loading loading-spinner loading-lg" />
          </div>
        )}

        {error && !loading && (
          <div role="alert" className="alert alert-error">
            <span>{error}</span>
          </div>
        )}

        {exportToast && (
          <Toast
            message={exportToast.message}
            type={exportToast.type}
            onClose={() => setExportToast(null)}
            duration={5000}
          />
        )}

        {!loading && !error && startups.length === 0 && (
          <div className="text-center py-8">
            <p className="text-lg">{t('startups.empty')}</p>
          </div>
        )}

        {!loading && !error && startups.length > 0 && (
          <>
            <StartupsTable startups={startups} onViewClick={(s) => setSelectedStartup(s)} />

            {meta && meta.totalPages > 1 && (
              <div className="flex justify-center items-center gap-1 mt-4">
                <button
                  className="px-2 text-base-content/50 hover:text-base-content disabled:opacity-25 disabled:cursor-not-allowed"
                  disabled={!meta.hasPrevious}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  aria-label={t('pagination.previous')}
                >
                  &lt;
                </button>
                {getPageNumbers(currentPage, meta.totalPages).map((page, i) =>
                  page === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-base-content/40 select-none">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`px-2 min-w-[1.75rem] text-sm rounded hover:text-base-content${page === currentPage ? ' font-bold text-base-content' : ' text-base-content/50'}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  )
                )}
                <button
                  className="px-2 text-base-content/50 hover:text-base-content disabled:opacity-25 disabled:cursor-not-allowed"
                  disabled={!meta.hasNext}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  aria-label={t('pagination.next')}
                >
                  &gt;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StartupsDashboard;
